import { useState, useMemo, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { connect, useSelector } from 'react-redux'
import { fromZonedTime } from 'date-fns-tz'
import { useTranslation } from 'react-i18next'

import PaginationFooter from '../../../../../components/PaginationFooter/PaginationFooter'
import ResidentTasksDetailsModal from '../../ResidentTasksDetailsModal/ResidentTasksDetailsModal'
import ChecklistsTaskDetailsModal from '../../ChecklistsTaskDetailsModal/ChecklistsTaskDetailsModal'
import { selectActiveBuilding } from '../../../../../reducers/buildingSelectorReducer'
import {
  selectChecklistDashboardResidents,
  fetchChecklistDashboardResidents,
} from '../../../../../reducers/Organisation/checklistsReducer'

import ChecklistsNavbar from './ChecklistsNavbar'
import ChecklistsTable from './ChecklistsTable'

const ChecklistsDashboard = ({ checklistsResidents, fetchChecklistDashboardResidents }) => {
  const { t } = useTranslation(['checklists-dashboard'])
  const [isSiteLoading, setSiteLoading] = useState(false)
  const [search, setSearch] = useState('')
  const [showOnPage, setShowOnPage] = useState(25)
  const [pageNumber, setPageNumber] = useState(1)
  const [dateFilter, setDateFilter] = useState(null)
  const [paginationItemsCount, setPaginationItemsCount] = useState(0)
  const [selectedTab, setSelectedTab] = useState(null)
  const [selectedResident, setSelectedResident] = useState()
  const [selectedTask, setSelectedTask] = useState(null)
  const isMountRef = useRef(true)

  const filteredResidents = useMemo(() => {
    return checklistsResidents
  }, [checklistsResidents, pageNumber])

  const selectedBuilding = useSelector(selectActiveBuilding)

  const fetchHistory = async (query) => {
    if (!selectedBuilding) return
    if (query === undefined) {
      query = search
    }
    setSiteLoading(true)
    const requestData = {
      buildingId: selectedBuilding.id,
      page: query ? 1 : pageNumber,
      limit: showOnPage,
      status: (selectedTab && selectedTab.value) || undefined,
      search: query,
      moveInDate: dateFilter ? fromZonedTime(dateFilter, 'UTC').getTime() : undefined,
    }

    await fetchChecklistDashboardResidents(requestData)
      .then((count) => setPaginationItemsCount(count))
      .catch((error) => console.error(error.message))

    setSiteLoading(false)
  }

  // NOTE: Skips the debounced fetch on mount (isMountRef) since the other effect below already
  // fetches once selectedBuilding is ready - this one only reacts to the user changing `search`.
  useEffect(() => {
    if (isMountRef.current) {
      isMountRef.current = false
      return
    }

    const timeoutId = setTimeout(() => fetchHistory(search), 1000)
    return () => clearTimeout(timeoutId)
  }, [search])

  useEffect(() => {
    if (selectedBuilding) {
      fetchHistory()
    }
  }, [pageNumber, showOnPage, selectedTab, selectedBuilding, dateFilter])

  return (
    <div className="ctp-checklists">
      <div className="ctp-checklists__inner">
        <div className="ctp-checklists-wrapper">
          <div className={`ctp-checklists-wrapper__inner ${isSiteLoading ? 'is-loading' : ''}`}>
            <ChecklistsNavbar
              search={search}
              selectedTab={selectedTab}
              setDateFilter={setDateFilter}
              setSearch={(e) => {
                setSearch(e)
                setPageNumber(1)
              }}
              setSelectedTab={setSelectedTab}
              dateFilter={dateFilter}
            />
            <div className={`ctp-checklists-wrapper-main-table-and-footer-container`}>
              <ChecklistsTable
                selectedBuilding={selectedBuilding}
                data={filteredResidents}
                setSelectedResident={setSelectedResident}
              />
              {checklistsResidents?.length > 0 ? (
                <PaginationFooter
                  showOnPage={showOnPage}
                  pageNumber={pageNumber}
                  paginationItemsCount={paginationItemsCount}
                  setSiteLoading={setSiteLoading}
                  setShowOnPage={setShowOnPage}
                  setPageNumber={setPageNumber}
                />
              ) : null}
            </div>
          </div>
        </div>
        {selectedResident && selectedBuilding && (
          <ResidentTasksDetailsModal
            onClose={() => setSelectedResident()}
            setSelectedTask={setSelectedTask}
            resident={selectedResident}
            selectedBuilding={selectedBuilding}
            fetchHistory={fetchHistory}
          />
        )}
        {selectedTask && (
          <ChecklistsTaskDetailsModal
            {...selectedTask}
            onClose={() => setSelectedTask(null)}
            headerTitle={t('checklists-dashboard:taskDetailsTitle')}
          />
        )}
      </div>
    </div>
  )
}

ChecklistsDashboard.propTypes = {
  checklistsResidents: PropTypes.array,
  fetchChecklistDashboardResidents: PropTypes.func,
}

export default connect(
  (state) => {
    return {
      checklistsResidents: selectChecklistDashboardResidents(state),
    }
  },
  {
    fetchChecklistDashboardResidents,
  },
)(ChecklistsDashboard)
